/**
 * Minimal Store catalog client for the Discover screens — GET /v1/capsules.
 *
 * Ported subset of ato-web's store client (the PWA only needs listing + the
 * publisher-avatar helper). Talks to the same account API as the rest of the
 * PWA, resolved through the shared ConnectionProfile.
 */
import { analyticsIdentityHeaders } from "@/analytics/client";
import { accountApiBase, withConnectionAuth } from "../app/connection";


function apiBase(): string {
  return accountApiBase();
}

export type CapsuleRank = "popular" | "latest" | "trending";

export type CapsuleSummary = {
  id: string;
  slug: string;
  name: string;
  description: string;
  /** Flat per-launch price in cents (billing v2); 0/absent = free. */
  price?: number;
  currency?: string;
  short_description?: string;
  category?: string;
  /** Store taxonomy top-level category id (src/store_taxonomy.ts on the API side). */
  primary_category?: string | null;
  primaryCategory?: string | null;
  /** Freeform publisher-chosen keywords (distinct from the taxonomy above). */
  tags?: string[];
  /** "github" when `repository` is a GitHub URL — the only origin the tag
   *  fallback (RichAppCard) knows how to read topics from. */
  origin?: string;
  repository?: string;
  type?: string;
  /** CLI package downloads only — NOT a web execution count. See
   *  `run_count`/`runCount` for the card's "N runs" label. */
  downloads?: number;
  /** Server-observed successful executions, counted once on the first ready
   *  transition — distinct from `downloads`. GET /v1/capsules (list) returns
   *  camelCase; other routes return snake_case — both declared per this
   *  file's existing convention. */
  run_count?: number;
  runCount?: number;
  vouches_count?: number;
  vouchesCount?: number;
  /** Live comment count (soft-deleted excluded) — returned by GET
   *  /v1/capsules (list) since 2026-08; absent on older routes/deploys. */
  comments_count?: number;
  commentsCount?: number;
  surfaces?: string[];
  screenshots?: string[];
  icon?: string;
  store_icon?: string;
  store_cover_image?: string;
  cover_image?: string;
  /** GET /v1/capsules (list) returns this camelCase; the by-slug detail
   *  route returns created_at snake_case for the SAME field — both are
   *  declared so callers can read either without knowing which route they
   *  came from. */
  created_at?: string;
  createdAt?: string;
  // Ready-State snapshot eligibility signals (consumed by SnapshotStatus). The
  // listing exposes secrets/network under `capabilities`; the rest are
  // best-effort/forward-compatible (may be absent — then the API's 409 blocks).
  visibility?: string;
  secretsRequired?: boolean | number | null;
  secrets_required?: boolean | number | null;
  capabilities?: {
    secretsRequired?: boolean | number | null;
    secrets_required?: boolean | number | null;
    bindingsRequired?: boolean | null;
    bindings_required?: boolean | null;
  } | null;
  publisher?: {
    handle: string;
    display_name?: string | null;
    displayName?: string | null;
    verified?: boolean;
    github_id?: number | null;
    github_login?: string | null;
    image?: string | null;
    avatar_url?: string | null;
    githubId?: number | null;
    githubLogin?: string | null;
    avatarUrl?: string | null;
  };
  // Store Snapshotization v0 (#188/#189) — the single most-recently-updated
  // `capsule_snapshot_metadata` row's OBSERVED result for this capsule. Never
  // use `snapshot_inventory_eligibility` to imply readiness — it's a
  // manifest-level "looks buildable" guess (ato-api#185/#187), not a verified
  // build+restore-smoke result. See `src/api/snapshotReadyBadge.ts`.
  snapshot_status?: string | null;
  snapshot_inventory_eligibility?: string | null;
  /** Present on GET /v1/capsules/vouched responses: the viewer vouched this
   *  capsule. Seeds the card's vouch state without a per-card hydrate GET. */
  viewer_vouched?: boolean;
};

export type CapsulesListResponse = {
  capsules: CapsuleSummary[];
  next_cursor?: string | null;
  total?: number | null;
};

function toQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && `${value}`.length > 0) {
      usp.set(key, String(value));
    }
  }
  const q = usp.toString();
  return q ? `?${q}` : "";
}

/**
 * True for the FIRST capsules request of a page load, false forever after.
 *
 * The server cannot see a page load on its own — the service worker can serve
 * one with no request at all — so the page flags its first API call and the
 * server records `app_opened` from it. All the browser contributes is "this is
 * the first request of a load"; the timestamp, the environment and the identity
 * are still established server-side.
 */
let appOpenFlagPending = true;

function takeAppOpenFlag(): "app_open" | undefined {
  if (!appOpenFlagPending) return undefined;
  appOpenFlagPending = false;
  return "app_open";
}

/** Test seam. */
export function resetAppOpenFlagForTests(): void {
  appOpenFlagPending = true;
}

export async function listCapsules(
  params: {
    q?: string;
    category?: string;
    subcategory?: string;
    surface?: string;
    filter?: string;
    rank?: CapsuleRank;
    limit?: number;
    cursor?: string;
    signal?: AbortSignal;
  } = {},
): Promise<CapsulesListResponse> {
  const res = await fetch(
    `${apiBase()}/v1/capsules${toQuery({
      q: params.q,
      category: params.category,
      subcategory: params.subcategory,
      surface: params.surface,
      filter: params.filter,
      rank: params.rank,
      limit: params.limit,
      cursor: params.cursor,
      entry: takeAppOpenFlag(),
    })}`,
    { headers: { Accept: "application/json", ...analyticsIdentityHeaders() }, signal: params.signal },
  );
  if (!res.ok) {
    throw new Error(`listCapsules failed: ${res.status}`);
  }
  const json = (await res.json()) as {
    capsules?: CapsuleSummary[];
    next_cursor?: string | null;
    total?: number | null;
  };
  return {
    capsules: Array.isArray(json.capsules) ? json.capsules : [],
    next_cursor: json.next_cursor ?? null,
    total: json.total ?? null,
  };
}

export async function listLikedCapsules(
  params: {
    limit?: number;
    cursor?: string;
    filter?: string;
    signal?: AbortSignal;
  } = {},
): Promise<CapsulesListResponse> {
  const res = await fetch(
    `${apiBase()}/v1/capsules/vouched${toQuery({
      limit: params.limit,
      cursor: params.cursor,
      filter: params.filter,
    })}`,
    withConnectionAuth({
      headers: { Accept: "application/json" },
      signal: params.signal,
    }),
  );
  if (!res.ok) {
    throw new Error(`listLikedCapsules failed: ${res.status}`);
  }
  const json = (await res.json()) as {
    capsules?: CapsuleSummary[];
    next_cursor?: string | null;
  };
  return {
    capsules: Array.isArray(json.capsules) ? json.capsules : [],
    next_cursor: json.next_cursor ?? null,
    total: null,
  };
}

/**
 * Single-capsule lookup by BARE slug (no publisher segment) — GET
 * /v1/capsules/by-slug/:slug. Used by the App Page (`/a/<slug>`) to resolve
 * metadata for a cold/shared-link load with no prior feed fetch. Slugs are
 * only unique per-publisher, so this is a best-effort convenience lookup
 * (the server picks the oldest public match on a genuine collision) — it is
 * the canonical, user-facing shape for `/a/<slug>` on purpose (kept short
 * and shareable, unlike a two-segment `publisher/slug` path). The response
 * is a superset of `CapsuleSummary`'s fields, so it flows straight into the
 * existing `toRunFeedItem()` mapper. 404 → null (no public capsule has this
 * slug, e.g. a curated demo's non-capsule slug) rather than a thrown error,
 * since that's an expected, handled case here.
 */
export async function getCapsuleBySlug(
  slug: string,
  opts: { signal?: AbortSignal; from?: "search" } = {},
): Promise<CapsuleSummary | null> {
  if (!slug) return null;
  const res = await fetch(
    `${apiBase()}/v1/capsules/by-slug/${encodeURIComponent(slug)}${opts.from === "search" ? "?from=search" : ""}`,
    {
      headers: { Accept: "application/json", ...analyticsIdentityHeaders() },
      signal: opts.signal,
    },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`getCapsuleBySlug failed: ${res.status}`);
  }
  return (await res.json()) as CapsuleSummary;
}

/**
 * Publisher-scoped single-capsule lookup — GET /v1/capsules/by/:publisher/:slug.
 * The unambiguous counterpart to `getCapsuleBySlug` above: slugs are only
 * unique per-publisher, so the Store's own links (`/a/by/<publisher>/<slug>`,
 * minted by GET /v1/store/home) carry the publisher and resolve exactly one
 * capsule. Same 404 → null contract as the bare-slug lookup.
 */
export async function getCapsuleByPublisherSlug(
  publisher: string,
  slug: string,
  opts: { signal?: AbortSignal; from?: "search" } = {},
): Promise<CapsuleSummary | null> {
  if (!publisher || !slug) return null;
  const res = await fetch(
    `${apiBase()}/v1/capsules/by/${encodeURIComponent(publisher)}/${encodeURIComponent(slug)}${opts.from === "search" ? "?from=search" : ""}`,
    {
      headers: { Accept: "application/json", ...analyticsIdentityHeaders() },
      signal: opts.signal,
    },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`getCapsuleByPublisherSlug failed: ${res.status}`);
  }
  return (await res.json()) as CapsuleSummary;
}

export type StoreSubcategory = { id: string; label: string };
export type StoreCategory = {
  id: string;
  label: string;
  subcategories: StoreSubcategory[];
};

/**
 * The public Store controlled vocabulary — GET /v1/store/taxonomy (no auth,
 * cacheable per deploy). The submission wizard's `Category*` field reads this
 * rather than shipping a second hardcoded copy of the 12 categories.
 */
export async function fetchStoreTaxonomy(
  opts: { signal?: AbortSignal } = {},
): Promise<{ categories: StoreCategory[]; surfaces: string[] }> {
  const res = await fetch(`${apiBase()}/v1/store/taxonomy`, {
    headers: { Accept: "application/json" },
    signal: opts.signal,
  });
  if (!res.ok) throw new Error(`fetchStoreTaxonomy failed: ${res.status}`);
  const json = (await res.json()) as {
    categories?: unknown;
    surfaces?: unknown;
  };
  const categories = Array.isArray(json.categories)
    ? json.categories.flatMap((entry): StoreCategory[] => {
        const c = (entry ?? {}) as Record<string, unknown>;
        if (typeof c.id !== "string") return [];
        return [
          {
            id: c.id,
            label: typeof c.label === "string" ? c.label : c.id,
            subcategories: Array.isArray(c.subcategories)
              ? c.subcategories.flatMap((sub): StoreSubcategory[] => {
                  const s = (sub ?? {}) as Record<string, unknown>;
                  if (typeof s.id !== "string") return [];
                  return [
                    { id: s.id, label: typeof s.label === "string" ? s.label : s.id },
                  ];
                })
              : [],
          },
        ];
      })
    : [];
  const surfaces = Array.isArray(json.surfaces)
    ? json.surfaces.filter((s): s is string => typeof s === "string")
    : [];
  return { categories, surfaces };
}

export type StoreHomeHeroImage = {
  light_url: string;
  /** Null when the publisher supplied one artwork for both themes. */
  dark_url: string | null;
  alt: string;
  width: number;
  height: number;
};

export type StoreHomeHeroAction = {
  label: string;
  /** App-internal route ("/a/hextris?autostart=1", "/settings", …). */
  href: string;
};

export type StoreHomeHero = {
  revision_id: string;
  activation_seq: number;
  /** Only FeaturedHeroV1 exists today; the field exists so a future layout
   *  can be rejected by an old client instead of rendered wrong. */
  layout_version: 1;
  eyebrow: string;
  /** Optional: the admin may clear it, and the API then sends "". */
  kicker: string;
  title: string;
  body: string;
  image: StoreHomeHeroImage;
  primary_action: StoreHomeHeroAction;
  secondary_action: StoreHomeHeroAction | null;
};

function heroString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

/**
 * Actions are admin-authored strings, so the client never lets one become an
 * off-site navigation: only app-internal paths are accepted, anything else is
 * dropped. A single leading slash is not enough — `//evil.example` is
 * protocol-relative and `/redirect?to=https://evil.example` smuggles a scheme
 * through, so both shapes are rejected outright.
 */
function heroAction(value: unknown): StoreHomeHeroAction | null {
  const a = (value ?? {}) as Record<string, unknown>;
  const label = heroString(a.label);
  const href = heroString(a.href);
  if (!label || !href) return null;
  if (!href.startsWith("/") || href.startsWith("//") || href.includes("://")) {
    return null;
  }
  return { label, href };
}

function heroImage(value: unknown): StoreHomeHeroImage | null {
  const i = (value ?? {}) as Record<string, unknown>;
  const lightUrl = heroString(i.light_url);
  if (!lightUrl) return null;
  return {
    light_url: lightUrl,
    dark_url: heroString(i.dark_url),
    alt: typeof i.alt === "string" ? i.alt : "",
    width: typeof i.width === "number" ? i.width : 0,
    height: typeof i.height === "number" ? i.height : 0,
  };
}

function parseHero(value: unknown): StoreHomeHero | null {
  const h = (value ?? {}) as Record<string, unknown>;
  // Only FeaturedHeroV1 is renderable here, and an unknown layout must not be
  // guessed at — a future layout arrives as "no hero", not a broken one.
  if (h.layout_version !== 1) return null;
  const revisionId = heroString(h.revision_id);
  const eyebrow = heroString(h.eyebrow);
  const title = heroString(h.title);
  const image = heroImage(h.image);
  const primaryAction = heroAction(h.primary_action);
  if (!revisionId || !eyebrow || !title || !image || !primaryAction) {
    return null;
  }
  return {
    revision_id: revisionId,
    activation_seq: typeof h.activation_seq === "number" ? h.activation_seq : 0,
    layout_version: 1,
    eyebrow,
    // The kicker is optional (an admin can clear it), so an empty string is a
    // valid published state — never a reason to drop the whole hero.
    kicker: typeof h.kicker === "string" ? h.kicker : "",
    title,
    body: typeof h.body === "string" ? h.body : "",
    image,
    primary_action: primaryAction,
    secondary_action: heroAction(h.secondary_action),
  };
}

/**
 * The admin-published Home editorial hero — GET /v1/store/home (no auth,
 * cacheable). `hero: null` means nothing is published right now, and a
 * malformed/unknown-layout payload is treated the same way: the Home route
 * simply renders without a hero rather than showing a broken one.
 */
export async function fetchStoreHome(
  opts: { signal?: AbortSignal } = {},
): Promise<{ version: 1; hero: StoreHomeHero | null }> {
  const res = await fetch(`${apiBase()}/v1/store/home`, {
    headers: { Accept: "application/json" },
    signal: opts.signal,
  });
  if (!res.ok) throw new Error(`fetchStoreHome failed: ${res.status}`);
  const json = (await res.json()) as { version?: unknown; hero?: unknown };
  // An envelope this client doesn't know how to read is treated as "nothing
  // published" rather than parsed on a guess.
  if (json.version !== 1) return { version: 1, hero: null };
  return { version: 1, hero: parseHero(json.hero) };
}

/** Direct avatar → GitHub avatar → Ato mark. */
export function resolvePublisherAvatarUrl(
  publisher:
    | {
        handle?: string;
        github_id?: number | null;
        github_login?: string | null;
        image?: string | null;
        avatar_url?: string | null;
      }
    | null
    | undefined,
): string {
  if (!publisher) return "/favicon.png";
  for (const candidate of [publisher.image, publisher.avatar_url]) {
    const value = (candidate ?? "").trim();
    if (!value) continue;
    try {
      const parsed = new URL(value);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") return parsed.toString();
    } catch {
      // ignore
    }
  }
  if (typeof publisher.github_id === "number" && publisher.github_id > 0) {
    return `https://avatars.githubusercontent.com/u/${publisher.github_id}?v=4`;
  }
  const login = (publisher.github_login ?? "").trim();
  if (login) return `https://github.com/${encodeURIComponent(login)}.png?size=256`;
  return "/favicon.png";
}
